import { z } from "zod";

// Note: Default times are now dynamically determined based on category in the component

// Schema for availability slots (now at space level)
const availabilitySchema = z.object({
  enabled: z.boolean(),
  startTime: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format"),
  endTime: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format"),
});

// Schema for coordinates
const coordinatesSchema = z.object({
  latitude: z.union([z.number(), z.string()]).transform(val => {
    const num = typeof val === "string" ? parseFloat(val) : val;
    return isNaN(num) ? 0 : num;
  }),
  longitude: z.union([z.number(), z.string()]).transform(val => {
    const num = typeof val === "string" ? parseFloat(val) : val;
    return isNaN(num) ? 0 : num;
  }),
  address: z.string().optional(),
});

// Schema for space instances
const instanceSchema = z.object({
  id: z.union([z.number(), z.string()]),
  apiId: z.number().optional(),
  name: z.string().min(1, "Unit name is required"),
  buildingAddressId: z
    .number()
    .nullable()
    .refine(val => val !== null, "Building address is required"),
  floorLevel: z.string().min(1, "Floor level is required"),
  floorId: z
    .number({ required_error: "Floor selection is required" })
    .nullable()
    .refine(val => val !== null, "Floor selection is required"),
  coordinates: coordinatesSchema,
});

// Schema for uploaded images
const imageSchema = z.object({
  id: z.union([z.string(), z.number()]),
  file: z.any().optional(),
  url: z.string(),
  name: z.string(),
  size: z.number().optional(),
});

// Schema for floor plan layout elements
const floorPlanElementSchema = z.object({
  i: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  elementType: z.string(),
  label: z.string().optional(),
});

// Schema for floor plan
const floorPlanSchema = z
  .object({
    layout: z.array(floorPlanElementSchema),
    timestamp: z.string(),
  })
  .nullable();

// Main form schema
export const addSpaceSchema = z
  .object({
    // Basic space information
    // spaceType is removed from UI but always passed as "LOW" in payload
    category: z
      .string()
      .optional()
      .transform(val => (val === "" ? undefined : val))
      .refine(val => !val || ["WORK", "STAY", "PLAY"].includes(val), "Please select a valid category"),
    categoryId: z
      .union([z.number(), z.null(), z.undefined(), z.nan()])
      .optional()
      .transform(val => {
        if (val === null || (typeof val === "number" && isNaN(val))) {
          return undefined;
        }
        return val;
      }),
    subCategoryId: z
      .union([z.number(), z.null(), z.undefined(), z.nan()])
      .optional()
      .transform(val => {
        if (val === null || (typeof val === "number" && isNaN(val))) {
          return undefined;
        }
        return val;
      }),
    capacity: z
      .string()
      .min(1, "Capacity is required")
      .refine(val => {
        const num = parseInt(val);
        return num > 0;
      }, "Capacity must be a positive number"),
    size: z
      .string()
      .min(1, "Size is required")
      .refine(val => {
        // Accept formats like "40 sqm", "40sqm", "40", etc.
        const cleanVal = val.toString().replace(/[^\d.]/g, "");
        const num = parseFloat(cleanVal);
        return num > 0 && num <= 10000;
      }, "Size must be a positive number"),
    description: z
      .string()
      .min(10, "Description must be at least 10 characters")
      .max(250, "Description must be 250 characters or less"),

    // Pricing information
    currency: z.string().min(3, "Currency is required").max(3, "Currency code must be 3 characters"),
    basePrice: z
      .string()
      .min(1, "Base price is required")
      .refine(val => {
        const num = parseFloat(val);
        return num > 0;
      }, "Base price must be greater than 0"),
    bookingDuration: z.enum(["hourly", "daily", "weekly", "monthly"], "Invalid booking duration"),
    minDuration: z
      .string()
      .min(1, "Minimum duration is required")
      .refine(val => {
        const num = parseInt(val);
        return num > 0;
      }, "Minimum duration must be greater than 0"),
    maxDuration: z
      .string()
      .min(1, "Maximum duration is required")
      .refine(val => {
        const num = parseInt(val);
        return num > 0;
      }, "Maximum duration must be greater than 0"),
    bufferTime: z.string().default("30"),

    // Space-level availability (moved from instance level)
    availability: z
      .object({
        monday: availabilitySchema,
        tuesday: availabilitySchema,
        wednesday: availabilitySchema,
        thursday: availabilitySchema,
        friday: availabilitySchema,
        saturday: availabilitySchema,
        sunday: availabilitySchema,
      })
      .optional(),

    // Visibility
    isPublic: z.boolean().default(true),
    visibleToCorporate: z.boolean().default(false),
    visibleToConsumer: z.boolean().default(false),

    // Host display settings
    hostDisplayName: z.string().optional(),
    hostDisplayImage: z.string().optional(),

    // Amenities (array of amenity IDs)
    amenities: z.array(z.number()).default([]).optional(),

    // Addons (array of addon IDs)
    addons: z.array(z.number()).default([]).optional(),

    // Images (optional)
    images: z.array(imageSchema).max(8, "Maximum 8 images allowed").default([]),

    // Instances (must have at least one, simplified structure)
    instanceGenerationMode: z.enum(["INDIVIDUAL", "BULK"]).default("INDIVIDUAL"),
    instanceQuantity: z.union([z.number().min(1).max(50), z.string().length(0)]).default(1),
    instances: z.array(instanceSchema).min(1, "At least one space unit is required"),

    // Floor plan (optional)
    floorPlan: floorPlanSchema.optional(),

    // Note: Tickets are exclusive to PLAY services and handled in play-services flow
  })
  .refine(
    data => {
      // Custom validation: maxDuration should be greater than minDuration
      const minDur = parseInt(data.minDuration);
      const maxDur = parseInt(data.maxDuration);
      return maxDur >= minDur;
    },
    {
      message: "Maximum duration must be greater than or equal to minimum duration",
      path: ["maxDuration"],
    }
  )
  .refine(
    data => {
      // Only validate if category is not undefined and not empty
      return !data.category || data.category === "" || ["WORK", "STAY", "PLAY"].includes(data.category);
    },
    {
      message: "Please select a category",
      path: ["category"],
    }
  )
  .refine(
    data => {
      // Only validate if subCategoryId is provided and is a valid number
      return !data.subCategoryId || (typeof data.subCategoryId === "number" && data.subCategoryId > 0);
    },
    {
      message: "Please select a subcategory",
      path: ["subCategoryId"],
    }
  )
  .refine(
    data => {
      // Only validate availability if it exists and has at least one enabled day
      if (!data.availability) return true;

      // Check if any day is enabled
      const _hasEnabledDay = Object.values(data.availability).some(day => day && day.enabled);

      // Always return true for now to prevent initial validation errors
      // The validation will be handled at form submission time
      return true;
    },
    {
      message: "At least one day must be available",
      path: ["availability"],
    }
  )
  .refine(
    data => {
      if (!data.availability) return true;
      return Object.values(data.availability).every(dayData => {
        if (!dayData.enabled) return true;
        const [startH, startM] = dayData.startTime.split(":").map(Number);
        const [endH, endM] = dayData.endTime.split(":").map(Number);
        const start = startH * 60 + startM;
        const end = endH * 60 + endM;
        return end > start;
      });
    },
    {
      message: "End time must be after start time",
      path: ["availability"],
    }
  );

// Default form values - empty for production (user must fill all fields)
export const defaultFormValues = {
  // spaceType is not in form but always passed as "LOW" in payload
  category: undefined, // Let user select
  categoryId: undefined, // Let user select (will be set from serviceCategories)
  subCategoryId: undefined, // Let user select
  capacity: "",
  size: "",
  description: "",
  currency: "kes",
  basePrice: "",
  bookingDuration: "hourly",
  minDuration: "1",
  maxDuration: "24",
  bufferTime: "30",
  isPublic: true,
  visibleToCorporate: false,
  visibleToConsumer: false,
  hostDisplayName: "",
  hostDisplayImage: "",
  amenities: [],
  addons: [],

  // Space-level availability (moved from instance level)
  // Note: These are placeholder times - actual defaults are set in the component based on category
  availability: {
    monday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
    tuesday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
    wednesday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
    thursday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
    friday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
    saturday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
    sunday: {
      enabled: false,
      startTime: "00:00",
      endTime: "23:59",
    },
  },

  images: [],
  instanceGenerationMode: "INDIVIDUAL",
  instanceQuantity: 1,
  instances: [
    {
      id: "default-1",
      name: "",
      buildingAddressId: null,
      floorLevel: "",
      floorId: null,
      coordinates: { latitude: 0, longitude: 0 },
    },
  ],
  floorPlan: null,

  // No ticket defaults here; tickets only apply to PLAY services
};

export default addSpaceSchema;
